import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Guid } from "moo-shared";
import { catchError, map, of, Subject, Subscription, switchMap, take, throwError } from "rxjs";
import { AuthenticationApiService } from "../../../core/authentication/authentication-api.service";
import { IpTokenService } from "../../../core/authentication/iptoken.service";
import { OrganisationTokenUnavailableError } from "../../../core/errors/organisation-token-unavailable-error";
import { isEmptyString } from "../../../shared/helpers";
import { ButtonTile } from "../../../shared/models/button-tile.model";
import { OrganisationTile } from "../../../shared/models/organisation-tile.model";
import { NotificationBarComponent } from "../../../shared/notification-bar";
import { LoginAssistantService } from "../login-assistant.service";

@Component({
  selector: "app-organisation-list",
  templateUrl: "./organisation-list.component.html"
})
export class OrganisationListComponent implements OnInit, OnDestroy {
  @ViewChild("notificationBar", { static: false }) notificationBar!: NotificationBarComponent;
  organisationTiles: OrganisationTile[] = [];
  personnelTile = ButtonTile.createPersonnelTile(this.translateService.instant("login-assistant.common.username-password"));

  private organisationClick$ = new Subject<Guid>();
  private subscriptions = new Subscription();

  constructor(
    private readonly authenticationApiService: AuthenticationApiService,
    private readonly ipTokenService: IpTokenService,
    private readonly loginAssistantService: LoginAssistantService,
    private readonly translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.authenticationApiService.resetLaunchDarklyOrganisationContext();

    this.subscriptions.add(
      this.ipTokenService.getAvailableOrganisations().pipe(take(1)).subscribe((organisations) => {
        this.organisationTiles = [...organisations]
          .sort((a, b) => a.Name
            .localeCompare(b.Name, "nl", { sensitivity: "base" }))
          .map(organisation => OrganisationTile.fromDto(organisation));
      })
    );

    this.subscriptions.add(
      this.organisationClick$.pipe(
        switchMap((organisationKey: Guid) => {
          this.loginAssistantService.setSelectedOrganisationKey(organisationKey);
          return this.ipTokenService.selectOrganisationAndFetchToken(organisationKey).pipe(
            map((token: string) => !isEmptyString(token)),
            catchError((error: unknown) => {
              if (error instanceof OrganisationTokenUnavailableError) {
                return of(false);
              }

              return throwError(() => error);
            })
          );
        })
      ).subscribe((isTokenAvailable: boolean) => {
        if (!isTokenAvailable) {
          this.showOrganisationTokenUnavailableError();
          return;
        }

        this.loginAssistantService.navigateToGroups();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.organisationClick$.complete();
  }

  selectOrganisation(organisation: OrganisationTile | ButtonTile): void {
    if (organisation.key === ButtonTile.PersonnelTileKey) {
      this.selectPersonnel();
      return;
    }

    this.organisationClick$.next(organisation.key);
  }

  selectPersonnel(): void {
    this.loginAssistantService.clearOrganisation();
    this.loginAssistantService.navigateToLogin();
  }

  private showOrganisationTokenUnavailableError(): void {
    this.loginAssistantService.clearOrganisation();
    this.notificationBar.showErrorMessage({
      icon: "icon-meh",
      text: this.translateService.instant("login-assistant.organisation-list.token-not-available"),
      dismissible: true
    });
  }
}
